import { Fab, makeStyles, Tooltip, Typography } from "@material-ui/core";
import { useCallback, useEffect, useState } from "react";
import DataPanel from "../DataPanel";
import PricingDetails from "./PricingDetails";
import { ClassNameMap } from "@material-ui/styles";
import PriceUpdatesConfirmationDialog from "./PriceUpdatesConfirmationDialog";
import PricingBulkUpdateDialog from "./PricingBulkUpdateDialog";
import { PriceRecalculationResult } from "./PriceRecalculationResultBox";
import usePricingGroupApi, { PricingGroupElement } from "../../api/PricingGroupApi";
import { calculateUnitPriceBasingOnKilogramPrice } from "../../model/Materials";
import useMaterialApi, { Material } from "../../api/MaterialApi";
import { MaterialPricesPanelProperties } from "./PricingsPanel";
import useHttp from "../../security/Http.hook";

interface Properties
extends MaterialPricesPanelProperties {
  pricingGroupId?: number;
  lastViewRefresh?: Date;
}

type NumberToNumberMap = {[key: number]: number|null};

const foamMaterialPricesColumns = [
  {
    name: 'material',
    label: 'Materiał',
  },
  {
    name: 'unitPrice',
    label: 'Cena',
  },
  {
    name: 'materialUnitPriceMargin',
    label: 'Marża na centymetrze względem przeliczenia wagi',
  },
  {
    name: 'pricingGroupPriceVariation',
    label: 'Różnica względem wybranej grupy cenowej',
  }
];

const calculateVariationWithSign = (referenceValue: number, comparedValue: number|null) => {
  const variation = comparedValue === null || comparedValue === undefined ? null : referenceValue -  comparedValue;
  const variationSign
    = (variation === null || variation === 0) ? ''
    : variation < 0 ? '-' : '+';
  return variation === null ? '?' : `${variationSign} ${Math.abs(variation).toFixed(2)}`;
}

const mapPricingGroupElementAndPutToMaterialIdPriceMap = (
  materialIdPriceMap: NumberToNumberMap,
  pricingGroupElement: PricingGroupElement
) => {
  const materialId = pricingGroupElement.material.id;
  if (materialId !== undefined) {
    const unitCustomerPrice = pricingGroupElement.unitCustomerPrice
    materialIdPriceMap[materialId] = unitCustomerPrice === undefined ? null : unitCustomerPrice;
  }
  return materialIdPriceMap;
};

const mapMaterialAndPutToMaterialIdWeightDerivedUnitPriceMap = (
  materialIdUnitPriceMap: NumberToNumberMap,
  material: Material
) => {
  materialIdUnitPriceMap[material.id] = calculateUnitPriceBasingOnKilogramPrice(material);
  return materialIdUnitPriceMap;
};

const flattenPriceRecalculationResults = (priceRecalculationResults: PriceRecalculationResult[]) => {
  const flattenedPriceRecalculationResult: PriceRecalculationResult = {
    formPriceUpdates: [],
    shapePriceUpdates: []
  };
  priceRecalculationResults.forEach(priceRecalculationResult => {
    flattenedPriceRecalculationResult.formPriceUpdates
      = flattenedPriceRecalculationResult.formPriceUpdates.concat(priceRecalculationResult.formPriceUpdates);
    flattenedPriceRecalculationResult.shapePriceUpdates
      = flattenedPriceRecalculationResult.shapePriceUpdates.concat(priceRecalculationResult.shapePriceUpdates);
  });
  return flattenedPriceRecalculationResult;
};

const useStyles = makeStyles(theme => ({
  percentSymbol: {
    fontSize: theme.typography.fontSize * 1.5,
    fontWeight: 'bold'
  }
}));

const FoamMaterialPricesPanel = (properties: Properties) => {
  const classes = useStyles();
  const http = useHttp();
  const materialApi = useMaterialApi();
  const pricingGroupApi = usePricingGroupApi();
  const customerId = properties.customerId;
  const priceListId = properties.priceListId;
  const pricingGroupId = properties.pricingGroupId;
  const lastViewRefresh = properties.lastViewRefresh;
  const foamMaterialPricesResourcePath = `customers/${customerId}/price-lists/${priceListId}/material-prices`;
  const [priceUpdates, setPriceUpdates] = useState<PriceRecalculationResult|null>(null);
  const [pricingBulkUpdateDialogOpen, setPricingBulkUpdateDialogOpen] = useState<boolean>(false);
  const [pricingGroupMaterialIdPriceMap, setPricingGroupMaterialIdPriceMap] = useState<NumberToNumberMap>({});
  const [materialIdWeightDerivedUnitPriceMap, setMaterialIdWeightDerivedUnitPriceMap] = useState<NumberToNumberMap>({});

  const afterPricingSave = (data: any) => setPriceUpdates(data);

  const handleModifyPricingButtonClicked = (selectedPricingIds: number[]) =>
    setPricingBulkUpdateDialogOpen(true);

  const handlePricingChangeSaved = (
    pricingChangeInPercents: number,
    selectedPricingIds: number[],
    refreshDisplayedPricings: () => void
  ) => {
    http.patch(`/api/${foamMaterialPricesResourcePath}`, selectedPricingIds.map(pricingId => ({
        pricingId,
        changeInPercents: pricingChangeInPercents
      })))
      .then(priceRecalculationResults => setPriceUpdates(flattenPriceRecalculationResults(priceRecalculationResults)))
      .then(refreshDisplayedPricings)
      .catch(error => console.error(`Pricing change failed: ${error}`));
    setPricingBulkUpdateDialogOpen(false);
  };

  const handlePricingChangeCancelled = () => setPricingBulkUpdateDialogOpen(false);

  const preparePricingGroupElementMaterialPriceMap = (pricingGroupElements: PricingGroupElement[]) => {
    const materialIdPriceMap = pricingGroupElements.reduce(mapPricingGroupElementAndPutToMaterialIdPriceMap, {});
    setPricingGroupMaterialIdPriceMap(materialIdPriceMap);
  };

  const prepareMaterialWeightDerivedUnitPriceMap = (materials: Material[]) => {
    const materialIdUnitPriceMap = materials.reduce(mapMaterialAndPutToMaterialIdWeightDerivedUnitPriceMap, {});
    setMaterialIdWeightDerivedUnitPriceMap(materialIdUnitPriceMap);
  };

  const renderModifyPricingButton = (
    buttonClasses: ClassNameMap,
    getSelectedIds: () => number[],
    refreshDisplayedPricings: () => void
  ) => {
    const selectedPricingIds = getSelectedIds();
    return <>
      <Tooltip title="Zmień cenę wybranych pozycji o zadany procent">
        <Fab
          aria-label="change"
          className={buttonClasses.button}
          color="primary"
          disabled={selectedPricingIds.length < 1}
          size="medium"
          onClick={() => handleModifyPricingButtonClicked(selectedPricingIds)}
        >
          <Typography className={classes.percentSymbol}>%</Typography>
        </Fab>
      </Tooltip>
      <PricingBulkUpdateDialog
        open={pricingBulkUpdateDialogOpen}
        onPricingChangeSaved={pricingChangeInPercents => handlePricingChangeSaved(
          pricingChangeInPercents,
          selectedPricingIds,
          refreshDisplayedPricings)}
        onCancel={handlePricingChangeCancelled} />
    </>
  };

  const convertToPricingRow = useCallback((data: any) => {
    const material = data.material;
    const materialWeightDerivedUnitPriceVariation = calculateVariationWithSign(data.unitPrice,  materialIdWeightDerivedUnitPriceMap[material.id]);
    const pricingGroupPriceVariation = calculateVariationWithSign(data.unitPrice, pricingGroupMaterialIdPriceMap[material.id]);
    return {
      ...data,
      material: material.name,
      materialUnitPriceMargin: materialWeightDerivedUnitPriceVariation,
      pricingGroupPriceVariation: pricingGroupPriceVariation
    };
  }, [materialIdWeightDerivedUnitPriceMap, pricingGroupMaterialIdPriceMap]);

  useEffect(() => {
    if (pricingGroupId !== undefined) {
      pricingGroupApi
        .fetchPricingGroupElements(pricingGroupId)
        .then(preparePricingGroupElementMaterialPriceMap)
        .catch(error => console.warn(error));
      materialApi
        .fetchMaterials()
        .then(prepareMaterialWeightDerivedUnitPriceMap)
        .catch(error => console.warn(error));
    }
  }, [pricingGroupId]);

  return <>
    { priceUpdates && <PriceUpdatesConfirmationDialog priceUpdates={priceUpdates}/> }
    <DataPanel
      title='Cennik pianek'
      resource={ foamMaterialPricesResourcePath }
      columns={ foamMaterialPricesColumns }
      selectableRows='multiple'
      lastViewRefresh={ lastViewRefresh }
      renderDetails={(detailsProps) => <PricingDetails { ...{ ...detailsProps, customerId: customerId, priceListId: priceListId }} />}
      convert={ convertToPricingRow }
      afterSave={ afterPricingSave }
      renderCustomButtons={ renderModifyPricingButton }
    />
  </>
};

export default FoamMaterialPricesPanel;